import { useState } from 'react';
import type { EstadoSolicitud, Solicitud } from '@copathy/shared';
import { ErrorApi } from '../lib/api';
import { useSolicitudes, useDecidir } from '../lib/solicitudes';
import { usePuede } from '../lib/auth';
import { Boton } from '../componentes/Boton';
import { Modal } from '../componentes/Modal';
import { Check, Equis, Escudo, Reloj } from '../componentes/iconos';

type Pestana = EstadoSolicitud | 'todas';

const PESTANAS: { clave: Pestana; texto: string }[] = [
  { clave: 'pendiente', texto: 'Por revisar' },
  { clave: 'sin_verificar', texto: 'Sin confirmar' },
  { clave: 'aprobada', texto: 'Aprobadas' },
  { clave: 'rechazada', texto: 'Rechazadas' },
  { clave: 'todas', texto: 'Todas' },
];

const ETIQUETA: Record<EstadoSolicitud, { texto: string; clases: string }> = {
  sin_verificar: { texto: 'Sin confirmar', clases: 'bg-amber-50 text-amber-800' },
  pendiente: { texto: 'Por revisar', clases: 'bg-naranja-50 text-naranja-700' },
  aprobada: { texto: 'Aprobada', clases: 'bg-green-50 text-green-800' },
  rechazada: { texto: 'Rechazada', clases: 'bg-red-50 text-red-700' },
};

const fecha = (iso: string) =>
  new Date(iso).toLocaleDateString('es-VE', { day: '2-digit', month: 'short', year: 'numeric' });

/**
 * Control de acceso: la cola de solicitudes (D-022).
 *
 * Copathy decide quién entra. Es una pantalla de operación diaria, no un
 * trámite de una vez: si la cola se atrasa tres días, alguien no puede
 * responder el cuestionario de la sesión de mañana.
 */
export function PanelSolicitudes() {
  const [pestana, setPestana] = useState<Pestana>('pendiente');
  const [aRechazar, setARechazar] = useState<Solicitud | null>(null);
  const [error, setError] = useState<string | null>(null);

  const consulta = useSolicitudes(pestana);
  const { aprobar, rechazar } = useDecidir();
  const puedeDecidir = usePuede('solicitudes.decidir');

  async function alAprobar(s: Solicitud) {
    setError(null);
    try {
      await aprobar.mutateAsync(s.id);
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo aprobar. Intenta de nuevo.');
    }
  }

  /*
    Sin `<Contenedor>` ni encabezado propio: este panel vive DENTRO de la pantalla
    de Usuarios, que ya los pone. Una solicitud es una cuenta que todavía no
    existe, y tenerla en otra sección obligaba a recordar que hay dos sitios donde
    mirar quién entra a la plataforma.
  */
  return (
    <>
      <div className="mb-5 flex flex-wrap gap-2 border-b border-marino-100">
        {PESTANAS.map((p) => (
          <button
            key={p.clave}
            onClick={() => setPestana(p.clave)}
            className={`-mb-px border-b-2 px-3.5 py-2.5 text-sm font-semibold transition ${
              pestana === p.clave
                ? 'border-naranja-500 text-marino-800'
                : 'border-transparent text-marino-400 hover:text-marino-700'
            }`}
          >
            {p.texto}
          </button>
        ))}
      </div>

      {error && (
        <div role="alert" className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {consulta.isPending && <p className="py-16 text-center text-marino-400">Cargando…</p>}

      {consulta.isError && (
        <p className="py-16 text-center text-red-600">
          No pudimos cargar la cola. Recarga la página.
        </p>
      )}

      {consulta.data && consulta.data.datos.length === 0 && (
        <div className="rounded-2xl border border-marino-100 bg-white py-16 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-marino-50 text-marino-300">
            <Escudo size={24} />
          </div>
          <h2 className="font-semibold text-marino-700">
            {pestana === 'pendiente' ? 'No hay nada por revisar' : 'No hay solicitudes acá'}
          </h2>
          <p className="mt-1 text-sm text-marino-400">
            {pestana === 'pendiente'
              ? 'Cuando alguien confirme su correo, aparece en esta lista.'
              : 'Prueba con otra pestaña.'}
          </p>
        </div>
      )}

      <div className="space-y-3">
        {consulta.data?.datos.map((s) => (
          <article
            key={s.id}
            className="rounded-2xl border border-marino-100 bg-white p-5 transition hover:border-marino-200"
          >
            <div className="flex flex-wrap items-start gap-x-4 gap-y-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2.5">
                  <h2 className="font-semibold text-marino-800">{s.nombre}</h2>
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${ETIQUETA[s.estado].clases}`}
                  >
                    {ETIQUETA[s.estado].texto}
                  </span>
                </div>

                <p className="mt-1 text-sm text-marino-500">{s.correo}</p>

                <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                  <div className="flex gap-1.5">
                    <dt className="text-marino-400">Empresa:</dt>
                    <dd className="font-medium text-marino-700">{s.empresaNombre}</dd>
                  </div>
                  {s.cargoTexto && (
                    <div className="flex gap-1.5">
                      <dt className="text-marino-400">Cargo:</dt>
                      <dd className="text-marino-700">{s.cargoTexto}</dd>
                    </div>
                  )}
                  {s.areaTexto && (
                    <div className="flex gap-1.5">
                      <dt className="text-marino-400">Área:</dt>
                      <dd className="text-marino-700">{s.areaTexto}</dd>
                    </div>
                  )}
                  <div className="flex gap-1.5">
                    <dt className="text-marino-400">Solicitó:</dt>
                    <dd className="text-marino-700">{fecha(s.creadoEn)}</dd>
                  </div>
                </dl>

                {/*
                  El cargo y el área son lo que la persona ESCRIBIÓ. El mapeo al
                  catálogo de la empresa (D-013) todavía no está construido: hoy
                  se aprueba sin asignarlos, y se completan después.
                */}
                {s.estado === 'sin_verificar' && (
                  <p className="mt-3 flex items-center gap-2 text-sm text-amber-800">
                    <Reloj size={16} />
                    Todavía no confirmó su correo. No se puede aprobar.
                  </p>
                )}

                {s.estado === 'rechazada' && s.motivoRechazo && (
                  <p className="mt-3 text-sm text-marino-500">
                    <span className="text-marino-400">Motivo:</span> {s.motivoRechazo}
                  </p>
                )}

                {s.decididaEn && s.decididaPorNombre && (
                  <p className="mt-2 text-xs text-marino-400">
                    {s.estado === 'aprobada' ? 'Aprobada' : 'Rechazada'} por {s.decididaPorNombre}{' '}
                    el {fecha(s.decididaEn)}
                  </p>
                )}
              </div>

              {s.estado === 'pendiente' && puedeDecidir && (
                <div className="flex gap-2">
                  <Boton
                    variante="secundario"
                    onClick={() => setARechazar(s)}
                    className="px-3.5 py-2 text-sm"
                  >
                    <Equis size={16} /> Rechazar
                  </Boton>
                  <Boton
                    onClick={() => void alAprobar(s)}
                    cargando={aprobar.isPending && aprobar.variables === s.id}
                    className="px-3.5 py-2 text-sm"
                  >
                    <Check size={16} /> Aprobar
                  </Boton>
                </div>
              )}
            </div>
          </article>
        ))}
      </div>

      {consulta.data && consulta.data.total > consulta.data.datos.length && (
        <p className="mt-6 text-center text-sm text-marino-400">
          Mostrando {consulta.data.datos.length} de {consulta.data.total}.
        </p>
      )}

      <ModalRechazo
        solicitud={aRechazar}
        alCerrar={() => setARechazar(null)}
        alConfirmar={async (motivo) => {
          if (!aRechazar) return;
          setError(null);
          try {
            await rechazar.mutateAsync({ id: aRechazar.id, motivo });
            setARechazar(null);
          } catch (e) {
            setError(e instanceof ErrorApi ? e.message : 'No se pudo rechazar.');
          }
        }}
        enviando={rechazar.isPending}
      />
    </>
  );
}

function ModalRechazo({
  solicitud,
  alCerrar,
  alConfirmar,
  enviando,
}: {
  solicitud: Solicitud | null;
  alCerrar: () => void;
  alConfirmar: (motivo: string) => Promise<void>;
  enviando: boolean;
}) {
  const [motivo, setMotivo] = useState('');

  function cerrar() {
    setMotivo('');
    alCerrar();
  }

  return (
    <Modal
      titulo="Rechazar solicitud"
      abierto={solicitud !== null}
      hayCambios={motivo.length > 0}
      alCerrar={cerrar}
      pie={
        <>
          <Boton variante="secundario" onClick={cerrar}>
            Cancelar
          </Boton>
          <Boton
            onClick={() => void alConfirmar(motivo)}
            cargando={enviando}
            disabled={motivo.trim().length < 5}
          >
            Rechazar
          </Boton>
        </>
      }
    >
      {solicitud && (
        <>
          <p className="mb-4 text-sm text-marino-500">
            Vas a rechazar la solicitud de{' '}
            <strong className="text-marino-800">{solicitud.nombre}</strong> para{' '}
            <strong className="text-marino-800">{solicitud.empresaNombre}</strong>.
          </p>

          <label
            htmlFor="motivo"
            className="mb-1.5 block text-sm font-semibold text-marino-700"
          >
            Motivo
          </label>
          <textarea
            id="motivo"
            rows={4}
            autoFocus
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="No aparece en la lista de personal de la empresa."
            className="w-full rounded-2xl border border-marino-200 bg-white px-4 py-3 text-marino-900
              outline-none transition placeholder:text-marino-400/70
              focus:border-marino-700 focus:ring-4 focus:ring-marino-700/10"
          />
          {/* El motivo le llega a la persona: sin él no sabe qué corregir. */}
          <p className="mt-2 text-xs text-marino-400">
            Esto se le envía por correo. Escribe algo que le sirva para saber qué hacer.
          </p>

          <p className="mt-4 rounded-xl border border-marino-100 bg-marino-50/60 px-4 py-3 text-xs text-marino-500">
            No se crea ninguna cuenta. El correo queda libre para volver a registrarse, por si
            se equivocó de código.
          </p>
        </>
      )}
    </Modal>
  );
}
